'use client';

import { MarketCard, type MarketCardMarket } from '../cards/market';
import { getMarketOutcome, getMarketStatus, isMarketResolved } from '../utils/market';

export interface MarketEvent {
  id: string;
  slug: string;
  event_id: string;
  title: string;
  image: string;
  icon: string;
  total_volume: string;
  total_liquidity: string;
  end_date: string;
  active: number;
  closed: number;
  markets: NewsMarket[];

  category: string;
  description: string;
  ticker: string;
  cover: string;
  closed_time: string;
  volume_24hr: string;
}

export interface NewsMarket {
  market_id: string;
  market_question: string;
  market_icon?: string;
  description: string;
  outcome_yes: string;
  outcome_no: string;
  outcome_yes_price: string;
  outcome_no_price: string;
  last_trade_price?: string;
  market_active: number;
  group_item_title: string | null;
  volume_24h: string;
  price_change_24h: string | null;
  recommendation: string;
  closed_time?: string | null;
}

interface ImpactMarketProps {
  markets: MarketEvent[];
}

interface ImpactMarketCardItem {
  id: string;
  href: string;
  market: MarketCardMarket;
}

const FALLBACK_MARKET_IMAGE = '/image/polymarket.svg';

export default function ImpactMarket({ markets }: ImpactMarketProps) {
  const list = markets.filter((m) => m.markets && m.markets.length > 0).map(toImpactMarketCardItem);

  return (
    <div className="mx-auto flex flex-col gap-5">
      {list.map((item) => (
        <MarketCard
          key={item.id}
          marketEventId={item.market.marketEventId ?? item.id}
          className="hover:opacity-80 transition-opacity"
          href={item.href}
          market={item.market}
          showBookmark={false}
        />
      ))}
    </div>
  );
}

function toImpactMarketCardItem(event: MarketEvent): ImpactMarketCardItem {
  const firstMarket = event.markets[0];
  const eventSlug = event.slug || event.event_id || event.id;
  const yesPrice = toNumber(firstMarket.outcome_yes_price);
  const priceChange = toNumber(firstMarket.price_change_24h);
  const totalVolume = toNumber(event.volume_24hr);
  const title = event.title || firstMarket.market_question || '';
  const outcome = firstMarket.group_item_title || firstMarket.outcome_yes?.toUpperCase() || 'YES';

  return {
    id: firstMarket.market_id || event.event_id || event.id,
    href: `/predic/market?id=${eventSlug}`,
    market: {
      marketEventId: event.event_id || event.id,
      imageSrc: event.image || event.icon || firstMarket.market_icon || FALLBACK_MARKET_IMAGE,
      imageAlt: title,
      title,
      outcome,
      probability: yesPrice * 100,
      change: priceChange * 100,
      changePeriod: '24h',
      volume: totalVolume,
      category: event.category || '',
      statusLabel: getMarketStatus(firstMarket),
      resolvedOutcome: isMarketResolved(firstMarket) ? getMarketOutcome(firstMarket) : undefined,
    },
  };
}

function toNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}
